import React, { useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  AlertCircle, Upload, X, CheckCircle2, Loader2, ImageIcon, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import supportService, {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
} from '@/services/api/supportService'
import { CORRECTABLE_FIELDS } from '@/data/correctableFields'

interface SupportTicketFormProps {
  onSuccess?: () => void
}

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({ onSuccess }) => {
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [category, setCategory] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  const [emailUpdates, setEmailUpdates] = useState(true)
  const [screenshot, setScreenshot] = useState<File | null>(null)
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null)

  // Data correction fields
  const [selectedFields, setSelectedFields] = useState<string[]>([])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedId, setSubmittedId] = useState('')
  const [submittedIsDataCorrection, setSubmittedIsDataCorrection] = useState(false)
  const [error, setError] = useState('')

  const isDataProblem = category === 'data_problem'

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    )
  }

  const handleCategoryChange = (value: string) => {
    setCategory(value)
    // Reset data-correction state when switching away
    if (value !== 'data_problem') {
      setSelectedFields([])
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files (PNG, JPG, GIF, WebP) are allowed')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be under 5MB')
      return
    }

    setScreenshot(file)
    setError('')

    const reader = new FileReader()
    reader.onloadend = () => setScreenshotPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const removeScreenshot = () => {
    setScreenshot(null)
    setScreenshotPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (isDataProblem) {
      // --- Data Correction path ---
      if (selectedFields.length === 0 || !description.trim()) {
        setError('Please select at least one field and provide a description')
        return
      }

      setIsSubmitting(true)
      try {
        const result = await supportService.createDataCorrectionTicket({
          affected_fields: selectedFields,
          description: description.trim(),
          issue_type: 'profile-data',
          current_page: location.pathname,
          browser_info: navigator.userAgent,
        })

        setSubmittedId(result.ticket_id)
        setSubmittedIsDataCorrection(true)
        setSubmitted(true)

        // Reset form
        setCategory('')
        setSelectedFields([])
        setDescription('')
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to submit data correction request. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    } else {
      // --- Standard support ticket path ---
      if (!category || !subject.trim() || !description.trim()) {
        setError('Please fill in all required fields')
        return
      }

      setIsSubmitting(true)
      try {
        const result = await supportService.createTicket({
          category,
          subject: subject.trim(),
          description: description.trim(),
          priority,
          current_page: location.pathname,
          browser_info: navigator.userAgent,
          email_updates: emailUpdates,
          screenshot: screenshot || undefined,
        })

        setSubmittedId(result.ticket_id)
        setSubmittedIsDataCorrection(false)
        setSubmitted(true)

        // Reset form
        setCategory('')
        setSubject('')
        setDescription('')
        setPriority('medium')
        setEmailUpdates(true)
        removeScreenshot()
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to submit ticket. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-4">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
          submittedIsDataCorrection ? 'bg-amber-100' : 'bg-green-100'
        }`}>
          <CheckCircle2 className={`w-8 h-8 ${
            submittedIsDataCorrection ? 'text-amber-600' : 'text-green-600'
          }`} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {submittedIsDataCorrection ? 'Data Correction Request Submitted!' : 'Ticket Submitted!'}
        </h3>
        <div className="text-sm text-gray-600">
          Your {submittedIsDataCorrection ? 'data correction request' : 'support ticket'}{' '}
          <Badge variant="outline" className="font-mono">{submittedId}</Badge> has been created.
          {submittedIsDataCorrection
            ? ' The HR / F&O team will review and correct your data.'
            : ' Our team will review it shortly.'}
        </div>
        {!submittedIsDataCorrection && emailUpdates && (
          <p className="text-xs text-gray-500">You'll receive email updates on status changes.</p>
        )}
        <div className="flex gap-3 mt-2">
          <Button variant="outline" size="sm" onClick={() => {
            setSubmitted(false)
            setSubmittedIsDataCorrection(false)
          }}>
            Submit Another
          </Button>
          <Button size="sm" onClick={onSuccess}>
            View My Submissions
          </Button>
        </div>
      </div>
    )
  }

  const canSubmit = isDataProblem
    ? selectedFields.length > 0 && description.trim().length > 0
    : !!category && !!subject.trim() && !!description.trim()

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      {/* Category */}
      <div className="space-y-1.5">
        <Label htmlFor="ticket-category" className="text-sm font-medium">
          Category <span className="text-red-500">*</span>
        </Label>
        <Select value={category} onValueChange={handleCategoryChange}>
          <SelectTrigger id="ticket-category">
            <SelectValue placeholder="What do you need help with?" />
          </SelectTrigger>
          <SelectContent>
            {TICKET_CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isDataProblem ? (
        /* ========== DATA CORRECTION MODE ========== */
        <>
          <Alert className="border-amber-200 bg-amber-50">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-800">
              Select the fields that contain incorrect data and describe the issue.
              This will create a data correction request for the HR / F&O team.
            </AlertDescription>
          </Alert>

          {/* Field checkboxes */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Affected Fields <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CORRECTABLE_FIELDS.map(field => (
                <div
                  key={field.id}
                  className={`flex items-center space-x-2 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                    selectedFields.includes(field.id)
                      ? 'bg-amber-100 border-amber-400'
                      : 'bg-white border-gray-200 hover:border-amber-300'
                  }`}
                  onClick={() => handleFieldToggle(field.id)}
                >
                  <Checkbox
                    id={`field-${field.id}`}
                    checked={selectedFields.includes(field.id)}
                    onCheckedChange={() => handleFieldToggle(field.id)}
                    className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                  />
                  <Label
                    htmlFor={`field-${field.id}`}
                    className="text-sm cursor-pointer font-medium leading-tight"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedFields.length === 0 && category && (
              <p className="text-xs text-muted-foreground">Select at least one field</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ticket-description" className="text-sm font-medium">
              Describe the Issue <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ticket-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g., My role title shows as 'Administrator' but should be 'Senior Administrator'. My people leader is listed incorrectly."
              rows={4}
              className="resize-none"
            />
          </div>
        </>
      ) : (
        /* ========== STANDARD SUPPORT TICKET MODE ========== */
        <>
          {/* Subject */}
          <div className="space-y-1.5">
            <Label htmlFor="ticket-subject" className="text-sm font-medium">
              Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ticket-subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              maxLength={500}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ticket-description" className="text-sm font-medium">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ticket-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the issue in detail. Include steps to reproduce if applicable."
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Priority */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Priority</Label>
            <div className="flex gap-2">
              {TICKET_PRIORITIES.map(p => {
                const colors: Record<string, string> = {
                  low: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200',
                  medium: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200',
                  high: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200',
                  urgent: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
                }
                const active = priority === p.value
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                      active
                        ? colors[p.value]
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Screenshot Upload */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Screenshot (optional)</Label>
            {screenshotPreview ? (
              <div className="relative border rounded-lg overflow-hidden bg-gray-50">
                <img
                  src={screenshotPreview}
                  alt="Screenshot preview"
                  className="w-full max-h-32 object-contain"
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="px-3 py-1.5 text-xs text-gray-500 bg-white border-t flex items-center gap-1.5">
                  <ImageIcon className="w-3 h-3" />
                  {screenshot?.name} ({((screenshot?.size || 0) / 1024).toFixed(0)} KB)
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-200 rounded-lg p-4 flex flex-col items-center gap-2 text-gray-400 hover:border-[#29ABE2] hover:text-[#29ABE2] transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span className="text-xs">Click to upload a screenshot (PNG, JPG, GIF, WebP — max 5MB)</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Email Updates Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="email-updates"
              checked={emailUpdates}
              onChange={e => setEmailUpdates(e.target.checked)}
              className="rounded border-gray-300 text-[#15305D] focus:ring-[#29ABE2]"
            />
            <Label htmlFor="email-updates" className="text-xs text-gray-600 cursor-pointer">
              Email me when this ticket is updated
            </Label>
          </div>
        </>
      )}

      {/* Context info */}
      <div className="text-xs text-gray-400 flex items-center gap-1">
        Page: <code className="bg-gray-100 px-1 rounded">{location.pathname}</code>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={isSubmitting || !canSubmit}
        className={`w-full ${isDataProblem ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#15305D] hover:bg-[#1a4a8a]'}`}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          isDataProblem ? 'Submit Data Correction Request' : 'Submit Support Ticket'
        )}
      </Button>
    </form>
  )
}

export default SupportTicketForm
